import { createContext, useContext, useMemo, useState } from "react";

// Open/closed state for the employee-profile section cards. Every section is
// collapsed by default in view/edit mode (the page is a long one and most visits
// only need a section or two); create mode opens them, because a form you are
// filling in from scratch shouldn't hide its required fields behind 13 clicks.
//
// The state lives in EmployeeProfilePage rather than inside each card so the
// section nav can open a section before scrolling to it -- a nav click that
// landed on a collapsed header would look like a dead link.
export interface SectionCollapseValue {
  isOpen: (id: string) => boolean;
  toggle: (id: string) => void;
  open: (id: string) => void;
  /** Expand-all / collapse-all. Moves the fallback rather than listing ids, so
   *  it also covers sections that aren't in the nav (assets, statutory). */
  setAll: (open: boolean) => void;
  /** True only when nothing is collapsed -- drives the single toggle's label. */
  allOpen: boolean;
}

const SectionCollapseContext = createContext<SectionCollapseValue | null>(null);

interface CollapseState {
  /** What a section with no entry in `overrides` does. */
  fallback: boolean;
  overrides: Record<string, boolean>;
}

// Sections that start shut and ignore expand-all / collapse-all: only their own
// header (or a nav click) opens them. Change History is a long audit list.
const MANUAL_ONLY = new Set(["history"]);

export function useSectionCollapseState(defaultOpen: boolean): SectionCollapseValue {
  const [{ fallback, overrides }, setState] = useState<CollapseState>({ fallback: defaultOpen, overrides: {} });
  return useMemo(() => ({
    isOpen: (id) => overrides[id] ?? (MANUAL_ONLY.has(id) ? false : fallback),
    toggle: (id) => setState(p => ({ ...p, overrides: { ...p.overrides, [id]: !(p.overrides[id] ?? (MANUAL_ONLY.has(id) ? false : p.fallback)) } })),
    open: (id) => setState(p => ({ ...p, overrides: { ...p.overrides, [id]: true } })),
    setAll: (open) => setState(p => ({
      fallback: open,
      overrides: Object.fromEntries(Object.entries(p.overrides).filter(([id]) => MANUAL_ONLY.has(id))),
    })),
    allOpen: fallback && !Object.entries(overrides).some(([id, v]) => !MANUAL_ONLY.has(id) && v === false),
  }), [fallback, overrides]);
}

export const SectionCollapseProvider = SectionCollapseContext.Provider;

// Sections rendered outside a provider (or in a test) stay open, so a missing
// provider degrades to the old always-expanded page instead of a blank one.
const ALWAYS_OPEN: SectionCollapseValue = {
  isOpen: () => true, toggle: () => {}, open: () => {}, setAll: () => {}, allOpen: true,
};

export function useSectionCollapse(): SectionCollapseValue {
  return useContext(SectionCollapseContext) ?? ALWAYS_OPEN;
}
